// "Money Floxir caught" — ONE definition, shared by every screen that prints it.
//
// THE SOURCE OF TRUTH IS THE SERVER. `MONEY_CAUGHT_TYPES` in server/reporting.js is
// what the Reports screens total, and it is the considered version. This file mirrors
// it exactly. If one changes, change both — they are the same claim about the same
// money, and a reader who sees two figures for it stops trusting either.
//
// What it had drifted from: Home, Dashboard and SupplierDetail each carried their own
// copy reading ['price_higher','extra_charge','vat_error','duplicate'], which was
// wrong twice over:
//
//   • `billed_over_delivered` was missing. It is the engine's name for "they billed
//     for units that never turned up" — and on the demo workspace it is more than half
//     the money caught. Reports said $4,968.64; Home said $2,255.89; the Cases screen
//     showed all five bills. Three screens, one set of bills, three different answers.
//   • `duplicate` is not a type the engine writes. server/cases.js emits
//     `duplicate_invoice`; the plain `duplicate` string matched nothing.
//
// DELIBERATELY EXCLUDED, matching the server:
//   duplicate / duplicate_invoice — a blocked duplicate is reported as a COUNT
//     ("2 duplicate bills blocked"), never summed into the money total: its
//     discrepancy_amount is the whole bill, which is money not spent rather than
//     money recovered, and adding it would inflate the headline.
//   missing_po / missing_invoice / late_delivery — completeness and timing, not money.
export const MONEY_DISCREPANCY_TYPES = new Set([
  'price_higher',
  'extra_charge',
  'vat_error',
  'qty_short',              // carries a quantity, not an amount — contributes 0, kept for parity
  'billed_over_delivered',
]);

// Types the engine treats as a blocked duplicate. Counted, never totalled.
export const DUPLICATE_TYPES = new Set(['duplicate', 'duplicate_invoice']);

// Does this case represent money caught, with a figure we can actually stand behind?
// A qualifying type is not enough — the amount has to be present and positive, or the
// case adds a row to the count and nothing to the total.
export function isMoneyCaught(c) {
  return MONEY_DISCREPANCY_TYPES.has(c?.discrepancy_type) && Number(c?.discrepancy_amount || 0) > 0;
}
